// JobRecord (job page round 2, pages/job-page-r2): the office's page for one job, redesigned from Amir's
// twelve points of 26 Oct. Composed only of catalogued pieces, top to bottom:
//   1 the top bar: where it was opened from (a crumb, never the job's number again), the one main
//     button, More (a MenuButton; Copy a link is its first item) and the one Close;
//   2 the heading: the title (the scope's first line, or the job type), who it's for and where, each a
//     link, then one quiet line: the job type and its number (round 3);
//   3 the state line (StateLine), only when it says what the strip can't: one word (Paid, On hold,
//     Cancelled, waiting for review), money owing in bold, a problem in red (round 3);
//   4 the strip (StoryStrip, rich): every stop with its day and one line of who and how it ended, its
//     facts on hover; the dates of the visits, the next booked one marked next with its window and
//     tech; a visit's stop opens it in Visits, another its row in History;
//   5 the work, in a fixed order, each an Area open or one line by the state and the viewer: Job summary
//     (KeyValue), Visits (RecordRows), Review, Estimates (EstimateCards), Parts and materials, Invoice,
//     Photos and forms (every photo in one PhotoRoll, then its forms and files in one list), Notes and
//     messaging (Thread, the composer under the newest), History (HistoryFeed: Key moments newest
//     first, All one press away);
//   6 the side: Customer, Tasks, Site (getting in, the buzzer and directions for this job), Details;
//   7 over the page: the photo viewer and the page's dialogs (Complete, Cancel, Send back), which do
//     something; nothing opens only to show more.
// Everything opens in place (Amir 26 Oct: "the same thing we fixed in history, apply that logic to
// everything on this page"): a visit, a form, the signature and a History row each open their entry
// under them (EntryDetail inline, RecordRow's or HistoryItem's disclosure), several at once; the page may
// hold which are open (openRows) to keep them in its address. Opened from a visit on the board, that
// visit opens in place. "Go to visit 2" from an entry opens its row in Visits.
// Every section's body starts on one edge, and its rows put the when in History's time column, so the
// page reads down one line. It draws a JobRecordView (record-view.ts) and holds no data of its own.
// Before there's a job: the skeleton (nothing pretends to be a job: never "A job"), still loading,
// failed and not found, each with the way back. Reads only --jobrec-*.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Area } from '../../components/Area/Area.tsx';
import { Button } from '../../components/Button/Button.tsx';
import { Close } from '../../components/Close/Close.tsx';
import { Composer } from '../../components/Composer/Composer.tsx';
import { ContactRow } from '../../components/ContactRow/ContactRow.tsx';
import { ConfirmDialog } from '../../components/Dialog/Dialog.tsx';
import { EstimateCard, EstimateCards } from '../../components/EstimateCard/EstimateCard.tsx';
import { HistoryFeed, type HistoryEntry } from '../../components/HistoryFeed/HistoryFeed.tsx';
import { KeyValue } from '../../components/KeyValue/KeyValue.tsx';
import { LineTable } from '../../components/LineTable/LineTable.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { MenuButton } from '../../components/Menu/Menu.tsx';
import { PhotoRoll } from '../../components/PhotoRoll/PhotoRoll.tsx';
import { PhotoViewer } from '../../components/PhotoViewer/PhotoViewer.tsx';
import { RecordList, RecordRow } from '../../components/RecordRow/RecordRow.tsx';
import { Segmented } from '../../components/Segmented/Segmented.tsx';
import { SiteBlock } from '../../components/SiteBlock/SiteBlock.tsx';
import { Skeleton, SkeletonGroup } from '../../components/Skeleton/Skeleton.tsx';
import { StateLine, StateProblem, StateStrong } from '../../components/StateLine/StateLine.tsx';
import { TaskRow } from '../../components/TaskRow/TaskRow.tsx';
import { Thread } from '../../components/Thread/Thread.tsx';
import { Totals } from '../../components/Totals/Totals.tsx';
import { CompareTable } from '../CompareTable.tsx';
import { EntryDetail } from '../EntryDetail/EntryDetail.tsx';
import type { ActionLink, FixControl, JobAction, JobEntry, OnJobAction } from '../job-view.ts';
import { JobHeader, Sep } from '../JobHeader/JobHeader.tsx';
import { RecordLayout } from '../../patterns/RecordLayout/RecordLayout.tsx';
import type { JobRecordView } from '../record-view.ts';
import { ReviewFixList, type ReviewFix } from '../ReviewFixList/ReviewFixList.tsx';
import { SendBackDialog } from '../SendBackDialog.tsx';
import { StoryStrip } from '../StoryStrip/StoryStrip.tsx';
import { StuckBar } from '../StuckBar/StuckBar.tsx';
import './JobRecord.css';

/** The page before it has a job: loading (then still loading), failed, not found. */
export interface JobRecordWaiting {
  status: 'loading' | 'slow' | 'failed' | 'notfound';
  /** The number from the link. */
  number?: string;
  crumb: ActionLink;
  error?: string;
}

export type JobRecordProps = {
  onAction: OnJobAction;
  /**
   * The Site's map, the app's own (GoogleMap with the job's point, or its "needs a pin" line), the way
   * visitBlock hands in the board's drawing. The same slot as JobPage's, so the two merge as one.
   */
  siteMap?: ReactNode;
  /** The rows open in place (Visits, forms, History), when the app holds them (in its address); none:
   * the page holds them. */
  openRows?: readonly string[];
  onOpenRows?: (ids: string[]) => void;
} & ({ view: JobRecordView } | { waiting: JobRecordWaiting; onRetry?: () => void });

export function JobRecord(props: JobRecordProps) {
  if ('view' in props)
    return (
      <Ready
        view={props.view}
        onAction={props.onAction}
        siteMap={props.siteMap}
        openRows={props.openRows}
        onOpenRows={props.onOpenRows}
      />
    );
  return <Waiting {...props.waiting} onRetry={props.onRetry} onAction={props.onAction} />;
}

function ViewLink({ link, act }: { link: ActionLink; act: (a: JobAction) => void }) {
  return link.href ? (
    <Link href={link.href}>{link.label}</Link>
  ) : (
    <Link
      variant={link.isDanger ? 'danger' : undefined}
      onPress={() => act({ type: 'go', id: link.id, ...(link.target ? { target: link.target } : {}) })}
    >
      {link.label}
    </Link>
  );
}

function Ready({
  view: v,
  onAction,
  siteMap,
  openRows,
  onOpenRows,
}: {
  view: JobRecordView;
  onAction: OnJobAction;
  siteMap?: ReactNode;
  openRows?: readonly string[];
  onOpenRows?: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(v.open.map((id) => [id, true])),
  );
  // the rows open in place, several at once: the app's (its address) or the page's own
  const [ownRows, setOwnRows] = useState<string[]>(() => (v.openOnArrival ? [v.openOnArrival] : []));
  const rows = openRows ?? ownRows;
  const rowsNow = useRef(rows);
  rowsNow.current = rows;
  const setRows = (ids: string[]) => {
    rowsNow.current = ids;
    if (!openRows) setOwnRows(ids);
    onOpenRows?.(ids);
  };
  const toggleRow = (id: string) => {
    const r = rowsNow.current;
    setRows(r.includes(id) ? r.filter((x) => x !== id) : [...r, id]);
  };
  const openRow = (id: string) => {
    if (!rowsNow.current.includes(id)) setRows([...rowsNow.current, id]);
  };
  const [dialog, setDialogNow] = useState<null | 'complete' | 'cancel' | 'sendback'>(null);
  // each opening of a dialog is one attempt; a retry inside it carries the same number (QA-058)
  const [attempt, setAttempt] = useState(0);
  const setDialog = (d: typeof dialog) => {
    if (d) setAttempt((x) => x + 1);
    setDialogNow(d);
  };
  const [photo, setPhoto] = useState<string | null>(null);
  const [composeFirst, setComposeFirst] = useState<string | null>(null);
  const [histFrom, setHistFrom] = useState<string | undefined>();
  // a stop on the strip, or a link to a row: History opens it in place (the count jumps to it again)
  const [hit, setHit] = useState<{ id: string; n: number } | null>(null);
  const head = useRef<HTMLDivElement>(null);
  const toggle = (id: string) => (o: boolean) => setOpen((x) => ({ ...x, [id]: o }));
  const go = (id: string) => {
    setOpen((x) => ({ ...x, [id]: true }));
    requestAnimationFrame(() => {
      const el = document.getElementById(`r-${id}`);
      el?.scrollIntoView({ block: 'start' });
      el?.querySelector<HTMLElement>('.fl-area__toggle')?.focus({ preventScroll: true });
    });
  };
  const inHistory = (id: string) => v.history.entries.some((e) => e.id === id);
  const isVisit = (id: string) => v.visits.rows.some((x) => x.id === id);
  const isFile = (id: string) => !!v.photosForms?.files.some((f) => f.id === id);
  const toHistory = (id: string) => {
    setOpen((x) => ({ ...x, history: true }));
    setHit((h) => ({ id, n: (h?.n ?? 0) + 1 }));
  };
  const focusRow = (id: string) => {
    const el = document.getElementById(`r-${id}`);
    el?.scrollIntoView({ block: 'center' });
    (el?.querySelector<HTMLElement>('button') ?? el)?.focus({ preventScroll: true });
  };
  // anything that has more to show opens it in place: a visit or a form under its row (its area opened),
  // an estimate is its card, a History row opens there, a message is in Notes and messaging
  const show = (id: string) => {
    const area = isVisit(id) ? 'visits' : isFile(id) ? 'photos' : null;
    if (area) {
      setOpen((x) => ({ ...x, [area]: true }));
      if (v.details[id]) openRow(id);
      return requestAnimationFrame(() => focusRow(id));
    }
    if (v.estimates?.cards.some((c) => c.id === id)) return go('estimates');
    if (inHistory(id)) return toHistory(id);
    if (v.thread.messages.some((m) => m.id === id)) return go('thread');
  };
  // opened from a visit on the board: that visit, open in place, in view
  useEffect(() => {
    const id = v.openOnArrival;
    if (!id) return;
    openRow(id);
    requestAnimationFrame(() => focusRow(id));
  }, []);
  const act = (a: JobAction) => {
    if (a.type === 'go' && a.target?.startsWith('detail:')) return show(a.target.slice(7));
    if (a.type === 'go' && a.target?.startsWith('section:')) return go(a.target.slice(8));
    if (a.type === 'go' && a.id === 'cancel' && v.dialogs.cancel) return setDialog('cancel');
    if (a.type === 'go' && a.id === 'sendback' && v.review?.sendBackTo) return setDialog('sendback');
    void Promise.resolve(onAction(a)).catch(() => undefined);
  };
  /** A row's entry, opened in place under it (EntryDetail inline). */
  const entry = (id: string) => {
    const d = v.details[id];
    return d ? (
      <EntryDetail
        placement="inline"
        detail={d}
        onClose={() => undefined}
        onGo={show}
        onAction={(a) => act({ type: 'go', id: a.id, target: a.target })}
      />
    ) : null;
  };
  /** RecordRow's disclosure, for a row with more to show. */
  const inPlace = (id: string) =>
    v.details[id] ? { onToggle: () => toggleRow(id), isOpen: rows.includes(id), expanded: entry(id) } : {};
  const links = (list: ActionLink[] | undefined) =>
    list?.length ? list.map((l) => <ViewLink key={`${l.id}-${l.target ?? ''}`} link={l} act={act} />) : null;
  const n = v.review?.left ?? 0;

  // ---- the top
  const p = v.primary;
  const pressMain = () => {
    if (!p) return;
    if (p.id === 'complete')
      return n ? go('review') : v.dialogs.complete ? setDialog('complete') : act({ type: 'complete' });
    if (p.id === 'msgtechs') {
      setComposeFirst('techs');
      return go('thread');
    }
    if (p.id === 'reply') {
      setComposeFirst('text');
      return go('thread');
    }
    if (p.id === 'pay') return go('invoice');
    act({ type: 'go', id: p.id });
  };
  const main = (stuck?: boolean) =>
    p ? (
      <Button variant="primary" size={stuck ? 'sm' : undefined} onPress={pressMain}>
        {p.label}
      </Button>
    ) : null;
  const R = v.record;
  // the top says only what the strip can't: a word (Paid, On hold), money owing, a problem; else nothing
  const stateWord = R.status ?? R.owing;
  const stateNow: ReactNode = !((R.status && R.owing) || R.problem) ? null : (
    <>
      {R.status && R.owing ? <StateStrong>{R.owing}</StateStrong> : null}
      {R.problem ? (
        <>
          {R.status && R.owing ? '; ' : null}
          <StateProblem>
            {R.problem.link ? (
              <Link variant="danger" onPress={() => go(R.problem!.link!.target ?? 'review')}>
                {R.problem.text}
              </Link>
            ) : (
              R.problem.text
            )}
          </StateProblem>
        </>
      ) : null}
    </>
  );
  const entries: HistoryEntry[] = v.history.entries.map((e) => withExtra(e, setPhoto));
  const stops = v.strip.stops.map((x) => ({
    ...x,
    opens: isVisit(x.opens) ? 'Open the visit' : inHistory(x.id) ? 'Open it in History' : undefined,
  }));
  const strip = v.strip.stops.length ? (
    <StoryStrip
      isRich
      visitsAsStops
      stops={stops}
      isWaiting={v.strip.isWaiting}
      total={entries.length}
      onStop={(id) => {
        // a visit's stop opens the visit in Visits; any other its row in History
        const to = v.strip.stops.find((x) => x.id === id)?.opens;
        if (to && isVisit(to)) return show(to);
        if (inHistory(id)) return toHistory(id);
        if (to) show(to);
      }}
      // Full history is all of it, one press (Amir's point 12)
      onAll={() => {
        setHistFrom('all');
        go('history');
      }}
    />
  ) : undefined;

  // ---- the work
  // who sold it and whether a work order is on file are filing facts: they go to Details, and a summary
  // with nothing else isn't drawn (critic 3: a real imported job often holds only "Sold by")
  const FILING = new Set(['Sold by', 'Work order on file']);
  const filing = v.summary?.items.filter((x) => FILING.has(String(x.label))) ?? [];
  const J = v.summary
    ? { ...v.summary, items: v.summary.items.filter((x) => !FILING.has(String(x.label))) }
    : undefined;
  const summaryArea = J?.items.length ? (
    <Area
      id="r-summary"
      title="Job summary"
      summary={J.line}
      isOpen={!!open.summary}
      onOpenChange={toggle('summary')}
    >
      <KeyValue items={J.items} />
    </Area>
  ) : null;

  const V = v.visits.rows;
  const visitsArea = (
    <Area
      id="r-visits"
      title="Visits"
      count={V.length || undefined}
      summary={v.visits.summary}
      actions={links(v.visits.actions)}
      isOpen={!!open.visits}
      onOpenChange={toggle('visits')}
    >
      {V.length ? (
        <RecordList label="Visits">
          {V.map((x) => (
            <RecordRow
              key={x.id}
              id={`r-${x.id}`}
              when={x.gutter}
              title={x.line}
              sub={x.sub}
              end={x.word}
              problem={x.problem}
              cameFrom={x.cameFrom ? 'You came from this visit' : undefined}
              label={`${x.gutter}: ${x.line}, ${x.word}`}
              {...inPlace(x.id)}
            />
          ))}
        </RecordList>
      ) : (
        <p className="fl-jobrec__note">No visits booked on this job yet.</p>
      )}
    </Area>
  );

  const Rv = v.review;
  const fixControl = (id: string, f: FixControl | undefined): ReactNode =>
    !f ? null : f.kind === 'note' ? (
      <span className="fl-jobrec__muted">{f.text}</span>
    ) : f.kind === 'links' ? (
      <>
        {f.links.map((l) =>
          l.target?.startsWith('photo:') ? (
            <Link key={l.id} onPress={() => setPhoto(l.target!.slice(6))}>
              {l.label}
            </Link>
          ) : (
            <Link key={l.id} onPress={() => act({ type: 'fix', fixId: id, choice: l.id })}>
              {l.label}
            </Link>
          ),
        )}
      </>
    ) : (
      <Segmented
        label={f.label}
        selectedKey={f.selected ?? ''}
        onSelectionChange={(k) => act({ type: 'fix', fixId: id, choice: String(k) })}
        items={f.options}
      />
    );
  const fixRows: ReviewFix[] =
    Rv?.fixes.map((f, i) => ({
      id: f.id,
      n: i + 1,
      title: f.title,
      detail: f.detail,
      isCleared: f.isCleared,
      fix: fixControl(f.id, f.fix),
    })) ?? [];
  const reviewArea = Rv ? (
    <Area
      id="r-review"
      title="Review"
      summary={
        <>
          {Rv.summary.lead ? `${Rv.summary.lead} ` : ''}
          {Rv.summary.isBad ? <span className="fl-jobrec__bad">{Rv.summary.text}</span> : Rv.summary.text}
        </>
      }
      isOpen={!!open.review}
      onOpenChange={toggle('review')}
    >
      <ReviewFixList
        handback={Rv.handback}
        fixes={fixRows}
        allClear={Rv.allClear}
        clearLine={Rv.clearLine}
        foot={
          Rv.sendBackTo || Rv.more?.length ? (
            <>
              {Rv.sendBackTo ? (
                <Link onPress={() => setDialog('sendback')}>Send back to {Rv.sendBackTo}</Link>
              ) : null}
              {links(Rv.more)}
            </>
          ) : undefined
        }
      >
        {Rv.compare ? <CompareTable compare={Rv.compare} /> : null}
      </ReviewFixList>
    </Area>
  ) : null;

  const E = v.estimates;
  const estimatesArea = E ? (
    <Area
      id="r-estimates"
      title="Estimates"
      count={E.cards.length || undefined}
      summary={E.summary}
      actions={links(E.actions)}
      isOpen={!!open.estimates}
      onOpenChange={toggle('estimates')}
    >
      {E.cards.length ? (
        <EstimateCards label="The job’s estimates">
          {E.cards.map((c) => (
            <EstimateCard
              key={c.id}
              number={c.number}
              title={c.title}
              state={c.state}
              word={c.word}
              options={c.options}
              line={c.line}
              problem={c.problem}
              actions={c.actions.length ? links(c.actions) : undefined}
            />
          ))}
        </EstimateCards>
      ) : (
        // drawn like every other section: its chevron, and open it says so and offers New estimate
        <p className="fl-jobrec__note">{E.summary}</p>
      )}
    </Area>
  ) : null;

  const P = v.parts;
  const partsArea = P ? (
    <Area
      id="r-parts"
      title="Parts and materials"
      count={P.rows.length || undefined}
      summary={P.summary}
      actions={links(P.actions)}
      isOpen={!!open.parts}
      onOpenChange={toggle('parts')}
    >
      <LineTable
        label="The job’s parts and materials"
        columns={[
          { id: 'part', label: 'Part' },
          { id: 'from', label: 'From' },
          ...(P.showCost ? [{ id: 'cost', label: 'Cost', align: 'end' as const }] : []),
          { id: 'stands', label: 'Where it stands' },
        ]}
        rows={P.rows.map((x) => ({
          id: x.id,
          cells: { part: x.part, from: x.from, cost: x.cost ?? '', stands: x.stands },
        }))}
      />
      {P.note ? <p className="fl-jobrec__note">{P.note}</p> : null}
    </Area>
  ) : null;

  const I = v.invoice;
  const invoiceArea = I ? (
    <Area
      id="r-invoice"
      title="Invoice"
      summary={I.summary}
      actions={links(I.actions)}
      isOpen={!!open.invoice}
      onOpenChange={toggle('invoice')}
    >
      {I.lines.length ? (
        <LineTable
          label={`${I.number}’s lines`}
          columns={[
            { id: 'line', label: 'Line' },
            { id: 'qty', label: 'Qty', align: 'end' },
            { id: 'price', label: 'Price', align: 'end' },
            { id: 'amt', label: 'Amount', align: 'end' },
          ]}
          rows={I.lines.map((l) => ({
            id: l.id,
            cells: {
              line: l.note ? (
                <>
                  {l.label} <span className="fl-jobrec__muted">· {l.note}</span>
                </>
              ) : (
                l.label
              ),
              qty: l.qty,
              price: l.price,
              amt: l.amount,
            },
          }))}
        />
      ) : I.emptyNote ? (
        <p className="fl-jobrec__note">{I.emptyNote}</p>
      ) : null}
      {I.totals.length ? <Totals label={`${I.number}’s totals`} rows={I.totals} /> : null}
      {I.matchNote ? <p className="fl-jobrec__note">{I.matchNote.text}</p> : null}
      {I.margin ? (
        <p className="fl-jobrec__margin">
          Only you see this · cost <b>{I.margin.cost}</b> ({I.margin.parts}) · gross profit{' '}
          <b>{I.margin.profit}</b>
        </p>
      ) : null}
    </Area>
  ) : null;

  const F = v.photosForms;
  const photosArea = F ? (
    <Area
      id="r-photos"
      title="Photos and forms"
      summary={F.summary}
      actions={links(F.actions)}
      isOpen={!!open.photos}
      onOpenChange={toggle('photos')}
    >
      <PhotoRoll
        photos={F.photos}
        onOpen={setPhoto}
        label={`Job ${v.number}’s photos, newest first`}
        emptyText="No photos on this job yet. The techs take them on the iPad."
      />
      {F.files.length ? (
        <section className="fl-jobrec__files" aria-labelledby="r-files-head">
          <h3 id="r-files-head" className="fl-jobrec__subhead">
            Forms and files
          </h3>
          <RecordList label="Forms and files">
            {F.files.map((f) =>
              f.href ? (
                <RecordRow
                  key={f.id}
                  id={`r-${f.id}`}
                  when={f.at}
                  glyph={f.glyph ?? 'form'}
                  title={f.title}
                  sub={f.sub}
                  end={
                    <Link href={f.href} isExternal>
                      {f.word}
                    </Link>
                  }
                />
              ) : (
                <RecordRow
                  key={f.id}
                  id={`r-${f.id}`}
                  when={f.at}
                  glyph={f.glyph ?? 'form'}
                  title={f.title}
                  sub={f.sub}
                  end={f.isBad ? <span className="fl-jobrec__bad">{f.word}</span> : f.word}
                  label={`${f.title}, ${f.word}`}
                  {...inPlace(f.id)}
                />
              ),
            )}
          </RecordList>
        </section>
      ) : null}
    </Area>
  ) : null;

  const Th = v.thread;
  const modes = composeFirst
    ? [...Th.modes].sort((a, b) => (a.id === composeFirst ? -1 : b.id === composeFirst ? 1 : 0))
    : Th.modes;
  const threadArea = (
    <Area
      id="r-thread"
      title="Notes and messaging"
      count={Th.messages.length || undefined}
      summary={Th.isWaiting ? <span className="fl-jobrec__bad">{Th.summary}</span> : Th.summary}
      isOpen={!!open.thread}
      onOpenChange={toggle('thread')}
    >
      <Thread
        label={`Notes and messages on job ${v.number}`}
        messages={Th.messages}
        earlier={
          Th.earlier
            ? { count: Th.earlier, onShow: () => act({ type: 'go', id: 'thread-earlier' }) }
            : undefined
        }
        composer={
          modes.length ? (
            <Composer
              key={composeFirst ?? 'first'}
              modes={modes}
              label="Write a note or a message"
              onSend={(mode, text) => onAction({ type: 'compose', mode, text })}
            />
          ) : undefined
        }
      />
    </Area>
  );

  const H = v.history;
  const historyArea = (
    <Area
      id="r-history"
      title="History"
      count={entries.length || undefined}
      isOpen={!!open.history}
      onOpenChange={toggle('history')}
    >
      <HistoryFeed
        key={histFrom ?? 'first'}
        initialFilter={histFrom}
        entries={entries}
        filters={H.filters}
        today={H.today}
        cap={Infinity}
        keyOrder="newest"
        filterLook="tabs"
        hitId={hit?.id}
        hitSeq={hit?.n}
        openIds={rows.filter(inHistory)}
        onOpenChange={(ids) => setRows([...rowsNow.current.filter((x) => !inHistory(x)), ...ids])}
        hasEntry={(id) => !!v.details[id]}
        renderEntry={entry}
      />
    </Area>
  );

  // ---- the side
  const C = v.side.customer;
  const T = v.side.tasks;
  const S = v.side.site;
  const D = v.side.details;
  const side = (
    <>
      <Area id="r-customer" title="Customer" isOpen={!!open.customer} onOpenChange={toggle('customer')}>
        <p className="fl-jobrec__note">
          {C.note}
          {C.recordLink ? (
            <>
              {C.note ? ' · ' : ''}
              <ViewLink link={C.recordLink} act={act} />
            </>
          ) : null}
        </p>
        {C.contacts.length ? (
          <div>
            {C.contacts.map((c) => (
              <ContactRow
                key={c.id}
                value={c.value}
                note={c.note}
                actions={c.actions.map((a) =>
                  a.href
                    ? { label: a.label, href: a.href }
                    : {
                        label: a.label,
                        onPress: () =>
                          a.action && act({ type: 'go', id: a.action.id, target: a.action.target }),
                      },
                )}
              />
            ))}
          </div>
        ) : null}
      </Area>
      {T ? (
        <Area
          id="r-tasks"
          title="Tasks"
          count={T.rows.length || undefined}
          summary={T.summary}
          actions={T.add ? <ViewLink link={T.add} act={act} /> : undefined}
          isOpen={!!open.tasks}
          onOpenChange={toggle('tasks')}
        >
          {T.rows.length ? (
            <div>
              {T.rows.map((t) => (
                <TaskRow
                  key={t.id}
                  title={t.title}
                  who={t.who}
                  due={t.due}
                  isDone={t.isDone}
                  doneOn={t.doneOn}
                  isDisabled={!T.canChange}
                  onChange={(done) => act({ type: 'task', taskId: t.id, isDone: done })}
                />
              ))}
            </div>
          ) : (
            <p className="fl-jobrec__note">No open tasks on this job.</p>
          )}
        </Area>
      ) : null}
      <Area id="r-site" title="Site" isOpen={!!open.site} onOpenChange={toggle('site')}>
        <SiteBlock
          address={S.address}
          directionsHref={S.directionsHref}
          gettingIn={S.gettingIn}
          // a callback's job number goes to that job (QA-776)
          atAddress={S.atAddress.map((x) => ({
            label: x.link ? <ViewLink link={x.link} act={act} /> : x.label,
            value: x.value,
          }))}
          // the real map comes in through the slot; never the drawn sketch on a job
          map={siteMap}
          showMap={false}
          isAccessFirst
        />
      </Area>
      <Area
        id="r-details"
        title="Details"
        summary={D.summary}
        isOpen={!!open.details}
        onOpenChange={toggle('details')}
      >
        <KeyValue
          density="tight"
          // a fact the viewer may act on is its link: Customer PO opens the customer's PO (QA-776)
          items={[
            ...filing,
            ...D.items.map((x) => ({
              label: x.label,
              value: x.link ? <ViewLink link={x.link} act={act} /> : x.value,
            })),
          ]}
        />
      </Area>
    </>
  );

  return (
    <article className="fl-jobrec" aria-labelledby="job-title">
      <StuckBar
        title={v.title}
        word={R.word}
        extra={
          R.problem ? (
            <Link variant="danger" onPress={() => go('review')}>
              {R.problem.text}
            </Link>
          ) : R.owing ? (
            <b>{R.owing}</b>
          ) : undefined
        }
        actions={
          <>
            <Link onPress={() => go('history')}>History</Link>
            <Link onPress={() => window.scrollTo({ top: 0 })}>Top</Link>
            {main(true)}
          </>
        }
        watch={head}
      />
      <div ref={head}>
        <JobHeader
          titleId="job-title"
          crumb={<ViewLink link={v.crumb} act={act} />}
          title={v.title}
          who={
            <>
              {v.customer.link ? (
                <ViewLink link={{ ...v.customer.link, label: v.customer.name }} act={act} />
              ) : (
                <b>{v.customer.name}</b>
              )}
              <Sep />
              <Link onPress={() => go('site')}>{v.address}</Link>
            </>
          }
          meta={v.meta}
          state={
            stateWord || R.problem ? (
              <StateLine word={stateWord} now={stateNow} label="Where the job stands" />
            ) : undefined
          }
          main={main()}
          mainOffOnPhone={!!p?.hideOnPhone}
          actions={
            v.menu.length ? (
              <MenuButton
                label={`Job ${v.number}: more`}
                sections={v.menu}
                onAction={(id) => act({ type: 'go', id })}
                placement="bottom end"
              >
                More
              </MenuButton>
            ) : undefined
          }
          close={
            <Close
              placement="inline"
              label="Close the job"
              onPress={() => act({ type: 'go', id: 'close' })}
            />
          }
          strip={strip}
        />
      </div>
      {v.notice ? (
        <p
          className={v.notice.isError ? 'fl-jobrec__bad' : 'fl-jobrec__note'}
          role={v.notice.isError ? 'alert' : 'status'}
        >
          {v.notice.text}
        </p>
      ) : null}
      <RecordLayout
        sideLabel="Customer, site and details"
        main={
          <>
            {summaryArea}
            {visitsArea}
            {reviewArea}
            {estimatesArea}
            {partsArea}
            {invoiceArea}
            {photosArea}
            {threadArea}
            {historyArea}
          </>
        }
        side={side}
      />
      {v.dialogs.complete ? (
        <ConfirmDialog
          isOpen={dialog === 'complete'}
          onClose={() => setDialog(null)}
          title={v.dialogs.complete.title}
          confirmLabel="Complete the job"
          cancelLabel="Not yet"
          failText="It isn’t completed. Nothing changed; try again."
          onConfirm={() => onAction({ type: 'complete', attempt })}
        >
          <p>{v.dialogs.complete.body}</p>
        </ConfirmDialog>
      ) : null}
      {v.dialogs.cancel ? (
        <ConfirmDialog
          isOpen={dialog === 'cancel'}
          onClose={() => setDialog(null)}
          title={v.dialogs.cancel.title}
          confirmLabel="Cancel the job"
          cancelLabel="Keep the job"
          reason={{
            label: 'Why is it cancelled?',
            description: 'For the job’s history. The customer doesn’t see it.',
            placeholder: 'e.g. They went with someone else.',
            missing: 'Say why, so the next person to open the job knows.',
          }}
          onConfirm={(reason) => onAction({ type: 'cancel', reason, attempt })}
        >
          <p>{v.dialogs.cancel.body}</p>
        </ConfirmDialog>
      ) : null}
      {v.review?.sendBackTo ? (
        <SendBackDialog
          isOpen={dialog === 'sendback'}
          onClose={() => setDialog(null)}
          tech={v.review.sendBackTo}
          onSend={(reason, message) => onAction({ type: 'sendBack', reason, message, attempt })}
        />
      ) : null}
      <PhotoViewer
        photos={v.photosForms?.photos ?? v.photos?.photos ?? []}
        label={`Job ${v.number}’s photos`}
        openId={photo}
        onClose={() => setPhoto(null)}
        onChange={setPhoto}
        // who sees a photo, for whoever may change it (the round-1 page's switch)
        {...(v.photos?.canShare
          ? { onShareChange: (photoId: string, shared: boolean) => act({ type: 'share', photoId, shared }) }
          : {})}
      />
    </article>
  );
}

/** Before the job: what's known is real (the crumb, Close, the number from the link); the rest is bars. */
function Waiting({
  status,
  number,
  crumb,
  error,
  onRetry,
  onAction,
}: JobRecordWaiting & { onRetry?: () => void; onAction: OnJobAction }) {
  const act = (a: JobAction) => void Promise.resolve(onAction(a)).catch(() => undefined);
  // the bars show only after a short wait, so a fast load never flashes them
  const [shown, setShown] = useState(status !== 'loading');
  useEffect(() => {
    if (status !== 'loading') return;
    const t = window.setTimeout(() => setShown(true), 150);
    return () => window.clearTimeout(t);
  }, [status]);
  const close = (
    <Close placement="inline" label="Close the job" onPress={() => act({ type: 'go', id: 'close' })} />
  );
  const kicker = number ? `Job ${number}` : undefined;
  if (status === 'failed' || status === 'notfound')
    return (
      <article className="fl-jobrec" aria-labelledby="job-title">
        <JobHeader
          titleId="job-title"
          crumb={<ViewLink link={crumb} act={act} />}
          // the title says the number; no kicker to say it twice
          title={
            status === 'notfound'
              ? number
                ? `There’s no job ${number}`
                : 'There’s no such job'
              : number
                ? `Job ${number} didn’t load`
                : 'The job didn’t load'
          }
          who={
            <span
              className={status === 'failed' ? 'fl-jobrec__bad' : undefined}
              role={status === 'failed' ? 'alert' : 'status'}
            >
              {status === 'failed'
                ? (error ?? 'The server didn’t answer. Nothing changed.')
                : 'It may have been deleted, or the link is wrong. Search for it, or go back.'}
            </span>
          }
          main={
            status === 'failed' && onRetry ? (
              <Button variant="primary" onPress={onRetry}>
                Try again
              </Button>
            ) : undefined
          }
          close={close}
        />
      </article>
    );
  return (
    <article className="fl-jobrec" aria-labelledby="job-title" aria-busy="true">
      <JobHeader
        titleId="job-title"
        crumb={<ViewLink link={crumb} act={act} />}
        kicker={kicker}
        title={
          <SkeletonGroup
            label={`${status === 'slow' ? 'Still loading' : 'Loading'} ${number ? `job ${number}` : 'the job'}`}
          >
            {shown ? <Skeleton shape="title" width="long" /> : null}
          </SkeletonGroup>
        }
        who={shown ? <Skeleton shape="name" width="mid" /> : null}
        state={
          shown ? (
            <div className="fl-jobrec__skel-state">
              <Skeleton shape="line" width="mid" />
              <Skeleton shape="line" width="long" />
              {status === 'slow' ? (
                <p className="fl-jobrec__note">Still loading. The server is slow; it’s still trying.</p>
              ) : null}
            </div>
          ) : null
        }
        close={close}
      />
      {shown ? (
        <RecordLayout
          sideLabel="Customer, site and details"
          main={
            <div className="fl-jobrec__skel-col" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} shape="row" width="full" />
              ))}
            </div>
          }
          side={
            <div className="fl-jobrec__skel-col" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} shape="row" width="full" />
              ))}
            </div>
          }
        />
      ) : null}
    </article>
  );
}

function withExtra(e: JobEntry, open: (id: string) => void): HistoryEntry {
  const { photos, play, ...rest } = e;
  void play;
  if (photos?.length)
    return {
      ...rest,
      extra: <Link onPress={() => open(photos[0]!)}>{photos.length > 1 ? 'See them' : 'See it'}</Link>,
    };
  return rest;
}
